import { Injectable } from '@angular/core';
import { UrlSignature } from '../common/apiEndPoint';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { Person } from './person';
import { ResponseMessage } from '../common/ResponseMessage';

export interface LoginResponse {
  phoneNumber: string;
  name: string;
  message: string;
  tokenType: string;
  email: string;
  status: number;
  token: string;
}

@Injectable()
export class LoginService {
  constructor(private httpClient: HttpClient, private urlSignature: UrlSignature) {}

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'An unknown error occurred. Please try again.';
    let httpStatus = error.status;
    let backendError: any = {};

    // Check if error.error is a string (due to responseType: 'text')
    if (typeof error.error === 'string') {
      try {
        // Attempt to parse the raw text body (which should contain your {message, status})
        backendError = JSON.parse(error.error);
      } catch (e) {
        // If parsing fails, use the raw string as the message
        backendError = { message: error.error };
      }
    } else if (error.error && typeof error.error === 'object' && error.error !== null) {
      // Fallback for when Angular successfully parsed the JSON automatically
      backendError = error.error;
    }

    // Use the message and status from the parsed object
    if (backendError.message) {
      errorMessage = backendError.message;
    }
    if (backendError.status) {
      httpStatus = backendError.status;
    }

    // Handle Network Error (status 0)
    if (httpStatus === 0) {
      errorMessage = 'Network error. Please check your internet connection.';
    }

    // Throw the standardized object that the component expects
    return throwError(() => ({
      status: httpStatus,
      message: errorMessage,
      originalError: error,
    }));
  }

  getPassword(requestJson: string): Observable<ResponseMessage> {
    const url = this.urlSignature.getPasswordUrl;
    return this.httpClient.post(url, requestJson).pipe(
      map((response) => response as ResponseMessage),
      catchError(this.handleError.bind(this))
    );
  }

  sendEmail(mailingData: string): Observable<string> {
    const url = this.urlSignature.sendEmailURL;
    return this.httpClient
      .post(url, mailingData, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .pipe(
        map((response) => response as string),
        catchError(this.handleError.bind(this))
      );
  }

  getPersonDetails(userMail: string, password: string): Observable<Person> {
    const url = this.urlSignature.personAuthUrl;
    return this.httpClient.post(url, { email: userMail, password }).pipe(
      map((response) => response as Person),
      catchError(this.handleError.bind(this))
    );
  }

  // 👇 FIX APPLIED HERE: Sets responseType: 'text' to capture the raw error body
  authenticateUser(email: string, password: string): Observable<LoginResponse> {
    const url = this.urlSignature.loginpaymenturl;

    return this.httpClient
      .post<any>(
        url,
        { email, password },
        {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
          // Forces Angular to retrieve the raw text body on error
          responseType: 'text' as 'json',
        }
      )
      .pipe(
        // Map the successful text response back to a JSON object (if the request succeeds)
        map((response) => (typeof response === 'string' ? JSON.parse(response) : response) as LoginResponse),
        catchError(this.handleError.bind(this))
      );
  }

  getSenderEmailId() {
    return this.urlSignature.senderEmailId;
  }
  isAuthenticated(): boolean {
    return !!localStorage.getItem('token');
  }

  getUserData(): any {
    return {
      email: localStorage.getItem('usermail'),
      name: localStorage.getItem('userName'),
      phone: localStorage.getItem('userPhone'),
      token: localStorage.getItem('token'),
      tokenType: localStorage.getItem('tokenType'),
    };
  }

  logout(): void {
    localStorage.removeItem('usermail');
    localStorage.removeItem('token');
    localStorage.removeItem('userName');
    localStorage.removeItem('userPhone');
    localStorage.removeItem('tokenType');
    localStorage.removeItem('password');
  }

  getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('token');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
  }

  initiatePasswordReset(email: string): Observable<{ success: boolean }> {
    const mailingData = JSON.stringify({
      email: email,
      subject: 'Password Reset',
    });
    return this.sendEmail(mailingData).pipe(
      map(() => ({ success: true })),
      catchError(this.handleError.bind(this))
    );
  }
}
